import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlayIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { fetchDetectionRules, deleteDetectionRule, pollTestRunStatus } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Pagination from './Pagination';
import EditRuleDrawer from './EditRuleDrawer';
import CreateRuleDrawer from './CreateRuleDrawer';
import TestRunModal from './TestRunModal';
import Badge from './ui/Badge';
import { severityTone, ruleModeTone } from './ui/badgeTones';
import CopyButton from './CopyButton';
import { RefreshButton, LoadingCard } from './ui/LoadingState';

const DEFAULT_PAGE_SIZE = 10;

const sentence = (s) => {
  if (!s) return '';
  const str = String(s).toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function ColFilter({ value, onChange, options, allLabel = 'All', labelFn }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="w-full mt-1 bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-xs text-gray-300 focus:outline-none focus:border-blue-500 cursor-pointer"
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>{labelFn ? labelFn(o) : o}</option>
      ))}
    </select>
  );
}

export default function AlertRulesPage() {
  const navigate = useNavigate();
  // Phase 2 of MSP separation: MSP users may view the (filtered) rules list
  // but cannot create / edit / clone / delete / test-run. Only global admins
  // see the action affordances.
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');

  const [sortColumn, setSortColumn] = useState('ruleName');
  const [sortDirection, setSortDirection] = useState('asc');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [editingRule, setEditingRule] = useState(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [testRunRule, setTestRunRule] = useState(null);
  const [deletingRuleId, setDeletingRuleId] = useState(null);

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDetectionRules();
      setRules(data);
    } catch (err) {
      setError(err.message || 'Failed to load detection rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  // Poll for rules with active test runs and update their status in-place
  const ACTIVE_TEST_STATUSES = ['Queued', 'Starting', 'Running'];
  useEffect(() => {
    const activeRules = rules.filter(
      (r) => ACTIVE_TEST_STATUSES.includes(r.lastTestRunStatus) && r.lastTestJobId
    );
    if (activeRules.length === 0) return;

    const intervalId = setInterval(async () => {
      await Promise.all(activeRules.map(async (r) => {
        try {
          const result = await pollTestRunStatus(r.ruleId, r.lastTestJobId);
          const newStatus = result.status;
          if (newStatus && newStatus !== r.lastTestRunStatus) {
            setRules((prev) =>
              prev.map((rule) =>
                rule.ruleId === r.ruleId ? { ...rule, lastTestRunStatus: newStatus } : rule
              )
            );
          }
        } catch (_) { /* ignore transient poll errors */ }
      }));
    }, 5000);

    return () => clearInterval(intervalId);
  }, [rules]); // re-evaluates as statuses change; stops when no active rules remain

  // Filtered rules
  const filteredRules = useMemo(() => {
    let result = rules;

    if (modeFilter) result = result.filter((r) => r.mode === modeFilter);
    if (enabledFilter === 'true') result = result.filter((r) => r.enabled);
    if (enabledFilter === 'false') result = result.filter((r) => !r.enabled);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((r) =>
        (r.ruleName || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.ruleId || '').toLowerCase().includes(q) ||
        (r.legacyRuleId || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [rules, modeFilter, enabledFilter, searchQuery]);

  // Sorted rules
  const sortedRules = useMemo(() => {
    const sorted = [...filteredRules];
    sorted.sort((a, b) => {
      let aVal = a[sortColumn] ?? '';
      let bVal = b[sortColumn] ?? '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredRules, sortColumn, sortDirection]);

  // Paginated rules
  const totalPages = Math.max(1, Math.ceil(sortedRules.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRules = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedRules.slice(start, start + pageSize);
  }, [sortedRules, safePage, pageSize]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setCurrentPage(1);
  };

  const handleDeleteRule = async (rule) => {
    if (rule.mode === 'PROD') {
      alert('Cannot delete a PROD rule. Demote it to TEST first.');
      return;
    }
    const confirmed = window.confirm(
      `Permanently delete rule "${rule.ruleName}"?\n\nThis detection rule and its definition will be removed. Any existing alerts previously generated by this rule will NOT be affected.\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingRuleId(rule.ruleId);
    try {
      await deleteDetectionRule(rule.ruleId);
      await loadRules();
    } catch (err) {
      alert(`Failed to delete rule: ${err.message}`);
    } finally {
      setDeletingRuleId(null);
    }
  };

  const SortIcon = ({ col }) => {
    if (sortColumn !== col) return null;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="h-3 w-3 text-blue-400 inline ml-0.5" />
      : <ChevronDownIcon className="h-3 w-3 text-blue-400 inline ml-0.5" />;
  };

  // Summary counts
  const prodCount = rules.filter((r) => r.mode === 'PROD').length;
  const testCount = rules.filter((r) => r.mode === 'TEST').length;
  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <AdjustmentsHorizontalIcon className="h-7 w-7 text-blue-400" />
          <div>
            <h1 className="text-xl font-bold text-gray-100">Alert Rules</h1>
            <p className="text-sm text-gray-500">
              Manage detection rule definitions. Changes take effect on next pipeline run (every 30 min).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isGlobalAdmin && (
            <button
              onClick={() => setCreateDrawerOpen(true)}
              className="btn-primary flex items-center gap-2"
            >
              <SparklesIcon className="h-4 w-4" />
              Create Rule
            </button>
          )}
          <RefreshButton
            loading={loading}
            onClick={loadRules}
            className="btn-secondary flex items-center gap-2"
            iconClassName="h-4 w-4"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-300">{error}</p>
          </div>
          <button onClick={loadRules} className="text-xs text-red-400 hover:text-red-300 underline">Retry</button>
        </div>
      )}

      {/* Summary cards */}
      {!loading && !error && rules.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="card px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Rules</p>
            <p className="text-2xl font-bold text-gray-100">{rules.length}</p>
          </div>
          <div className="card px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">PROD</p>
            <p className="text-2xl font-bold text-green-400">{prodCount}</p>
          </div>
          <div className="card px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">TEST</p>
            <p className="text-2xl font-bold text-amber-400">{testCount}</p>
          </div>
          <div className="card px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Enabled</p>
            <p className="text-2xl font-bold text-blue-400">{enabledCount}</p>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            placeholder="Search by name, description, or rule ID..."
            className="input-field w-full pl-9"
          />
        </div>
        {!loading && (
          <span className="text-xs text-gray-500">{filteredRules.length} rule{filteredRules.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <LoadingCard message="Loading rules..." />
      ) : rules.length === 0 && !error ? (
        <div className="text-center py-20">
          <AdjustmentsHorizontalIcon className="h-12 w-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No detection rules found.</p>
          <p className="text-xs text-gray-600 mt-1">Click "Create Rule" to generate one with AI, or rules are created by the detection pipeline.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-800">
            <table className="w-full text-sm">
              <thead>
                {/* Column headers */}
                <tr className="bg-gray-800/60 text-gray-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => handleSort('ruleName')}>
                    Name <SortIcon col="ruleName" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => handleSort('severity')}>
                    Severity <SortIcon col="severity" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => handleSort('ruleType')}>
                    Type <SortIcon col="ruleType" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => handleSort('mode')}>
                    Mode <SortIcon col="mode" />
                  </th>
                  <th className="px-3 py-2 text-center cursor-pointer select-none" onClick={() => handleSort('enabled')}>
                    Enabled <SortIcon col="enabled" />
                  </th>
                  <th className="px-3 py-2 text-center">Ver</th>
                  <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => handleSort('updatedUtc')}>
                    Updated <SortIcon col="updatedUtc" />
                  </th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
                {/* Inline filters */}
                <tr className="bg-gray-800/30">
                  <th className="px-3 py-1" />
                  <th className="px-3 py-1" />
                  <th className="px-3 py-1" />
                  <th className="px-3 py-1">
                    <ColFilter
                      value={modeFilter}
                      onChange={handleFilterChange(setModeFilter)}
                      options={['PROD', 'TEST']}
                    />
                  </th>
                  <th className="px-3 py-1">
                    <ColFilter
                      value={enabledFilter}
                      onChange={handleFilterChange(setEnabledFilter)}
                      options={['true', 'false']}
                      labelFn={(v) => v === 'true' ? 'Yes' : 'No'}
                    />
                  </th>
                  <th className="px-3 py-1" />
                  <th className="px-3 py-1" />
                  <th className="px-3 py-1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {paginatedRules.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No rules match the current filters.
                    </td>
                  </tr>
                ) : (
                  paginatedRules.map((rule) => (
                    <tr key={rule.ruleId} className="group hover:bg-gray-800/40 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex items-center text-gray-200 font-medium" title={rule.ruleId}>
                          <span>{rule.ruleName}</span>
                          <CopyButton text={rule.ruleId} />
                        </div>
                        {(() => {
                          const includeCount = (rule.includedTenantIds || '').split(',').filter(Boolean).length;
                          const excludeCount = (rule.excludedTenantIds || '').split(',').filter(Boolean).length;
                          let providerCount = 0;
                          let titleCount = 0;
                          try {
                            const p = JSON.parse(rule.parametersJson || '{}');
                            if (Array.isArray(p.excluded_ip_providers)) providerCount = p.excluded_ip_providers.length;
                            if (Array.isArray(p.excluded_alert_titles)) titleCount = p.excluded_alert_titles.length;
                          } catch { /* ignore */ }
                          if (includeCount === 0 && excludeCount === 0 && providerCount === 0 && titleCount === 0) return null;
                          return (
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {includeCount > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-sky-900/30 text-sky-300 border border-sky-700/40 text-[10px]">
                                  Scoped to {includeCount} {includeCount === 1 ? 'tenant' : 'tenants'}
                                </span>
                              )}
                              {excludeCount > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-[10px]">
                                  Excludes {excludeCount} {excludeCount === 1 ? 'tenant' : 'tenants'}
                                </span>
                              )}
                              {providerCount > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-[10px]">
                                  Provider exclusions: {providerCount}
                                </span>
                              )}
                              {titleCount > 0 && (
                                <span className="px-1.5 py-0.5 rounded bg-rose-900/30 text-rose-300 border border-rose-700/40 text-[10px]">
                                  Excludes {titleCount} alert {titleCount === 1 ? 'title' : 'titles'}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={severityTone(rule.severity)} dot>{sentence(rule.severity)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-xs">
                        {rule.ruleType === 'ip_flag_match' ? 'IP Flag' : rule.ruleType === 'log_type_match' ? 'Log Type' : rule.ruleType === 'baseline_deviation' ? 'Baseline' : rule.ruleType === 'operation_baseline_deviation' ? 'Op+Baseline' : rule.ruleType === 'python_function_config' ? 'Config Fn' : 'Python Fn'}
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={ruleModeTone(rule.mode)} dot>{sentence(rule.mode)}</Badge>
                        {rule.mode === 'TEST' && rule.tested && (
                          <ClockIcon className="ml-1 h-3.5 w-3.5 inline text-green-400" title="Runs on schedule (every 30 min)" />
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {rule.enabled
                          ? <CheckCircleIcon className="h-4 w-4 text-green-400 inline" />
                          : <XCircleIcon className="h-4 w-4 text-red-400 inline" />}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-500">v{rule.version}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {rule.updatedUtc ? new Date(rule.updatedUtc).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        {!isGlobalAdmin && <span className="text-gray-600 text-xs">—</span>}
                        {isGlobalAdmin && (<>
                        <button
                          onClick={() => setEditingRule(rule)}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                          title="Edit rule"
                        >
                          <PencilSquareIcon className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => { setCloneSource(rule); setCreateDrawerOpen(true); }}
                          className="ml-2 text-sky-400/60 hover:text-sky-400 transition-colors"
                          title="Clone rule"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDeleteRule(rule)}
                          disabled={deletingRuleId === rule.ruleId || rule.mode === 'PROD'}
                          className={`ml-2 transition-colors ${
                            rule.mode === 'PROD'
                              ? 'text-gray-600 cursor-not-allowed'
                              : deletingRuleId === rule.ruleId
                                ? 'text-red-400 cursor-wait'
                                : 'text-red-400/60 hover:text-red-400'
                          }`}
                          title={rule.mode === 'PROD' ? 'Cannot delete PROD rules — demote to TEST first' : 'Delete rule'}
                        >
                          {deletingRuleId === rule.ruleId
                            ? <ArrowPathIcon className="h-4 w-4 inline animate-spin" />
                            : <TrashIcon className="h-4 w-4 inline" />}
                        </button>
                        {ACTIVE_TEST_STATUSES.includes(rule.lastTestRunStatus) ? (
                          <span
                            className="ml-2 inline-flex items-center text-amber-400"
                            title={`Test run in progress (${rule.lastTestRunStatus})`}
                          >
                            <ArrowPathIcon className="h-4 w-4 inline animate-spin" />
                          </span>
                        ) : (
                          <button
                            onClick={() => rule.mode !== 'PROD' && setTestRunRule(rule)}
                            disabled={rule.mode === 'PROD'}
                            className={`ml-2 transition-colors ${rule.mode === 'PROD' ? 'text-gray-600 cursor-not-allowed' : 'text-green-400 hover:text-green-300'}`}
                            title={rule.mode === 'PROD' ? 'Cannot run tests on PROD rules — demote to TEST first' : 'Test run'}
                          >
                            <PlayIcon className="h-4 w-4 inline" />
                          </button>
                        )}
                        {rule.lastTestRunStatus === 'Errored' && rule.lastTestJobId && (
                          <button
                            onClick={() => navigate(
                              `/admin/test-results/${encodeURIComponent(rule.ruleId)}/${encodeURIComponent(rule.lastTestJobId)}`,
                              { state: {
                                ruleName: rule.ruleName,
                                ruleType: rule.ruleType,
                                parametersJson: rule.parametersJson,
                                startDate: rule.lastTestStartDate,
                                endDate: rule.lastTestEndDate,
                                tenantId: rule.lastTestTenantId || null,
                                testRunId: rule.lastTestRunId,
                                viewOnly: true,
                              }}
                            )}
                            className="ml-2 text-orange-400 hover:text-orange-300 transition-colors"
                            title="Last test run errored — click for details"
                          >
                            <ExclamationTriangleIcon className="h-4 w-4 inline" />
                          </button>
                        )}
                        {rule.lastTestRunStatus === 'Completed' && rule.lastTestJobId && (
                          <button
                            onClick={() => navigate(
                              `/admin/test-results/${encodeURIComponent(rule.ruleId)}/${encodeURIComponent(rule.lastTestJobId)}`,
                              { state: {
                                ruleName: rule.ruleName,
                                ruleType: rule.ruleType,
                                parametersJson: rule.parametersJson,
                                startDate: rule.lastTestStartDate,
                                endDate: rule.lastTestEndDate,
                                tenantId: rule.lastTestTenantId || null,
                                testRunId: rule.lastTestRunId,
                                viewOnly: true,
                              }}
                            )}
                            className="ml-2 text-purple-400 hover:text-purple-300 transition-colors"
                            title={`View last test results (${rule.lastTestStartDate} to ${rule.lastTestEndDate})`}
                          >
                            <EyeIcon className="h-4 w-4 inline" />
                          </button>
                        )}
                        </>)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {sortedRules.length > 0 && (
            <div className="mt-3">
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={sortedRules.length}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              />
            </div>
          )}
        </>
      )}

      {/* Edit drawer */}
      <EditRuleDrawer
        isOpen={!!editingRule}
        rule={editingRule}
        onClose={() => setEditingRule(null)}
        onSuccess={loadRules}
      />

      {/* Create rule drawer */}
      <CreateRuleDrawer
        isOpen={createDrawerOpen}
        onClose={() => { setCreateDrawerOpen(false); setCloneSource(null); }}
        onSuccess={() => { loadRules(); setCloneSource(null); }}
        cloneSource={cloneSource}
      />

      {/* Test run modal */}
      <TestRunModal
        isOpen={!!testRunRule}
        rule={testRunRule}
        onClose={() => setTestRunRule(null)}
      />
    </div>
  );
}
